<script lang="ts" module>
  import { CLIENT_COLORS } from '../theme/palette';
  /** @deprecated import CLIENT_COLORS from lib/theme/palette. */
  export const SWATCHES = CLIENT_COLORS;
</script>

<script lang="ts">
  // Colour picker for clients (and anything else that needs a colour).
  //   <ColorSwatches bind:value={color} />                     client colours
  //   <ColorSwatches bind:value={accent} colors={…} />         any list of hex colours
  // Arrow keys move the selection (radio group).
  import { Check } from '@lucide/svelte';

  interface Props {
    value?: string | null;
    colors?: string[];
    /** Names for the colours (accessible labels), same order as `colors`. */
    labels?: string[];
    ariaLabel?: string;
    size?: 'sm' | 'md';
    onchange?: (v: string | null) => void;
  }
  let { value = $bindable(null), colors = CLIENT_COLORS, labels, ariaLabel, size = 'md', onchange }: Props = $props();
  let root: HTMLElement | null = $state(null);
  const focusIndex = $derived(Math.max(0, colors.findIndex((c) => c.toLowerCase() === (value || '').toLowerCase())));

  function pick(color: string) {
    value = color;
    onchange?.(color);
  }
  function keydown(event: KeyboardEvent, index: number) {
    const delta = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const next = (index + delta + colors.length) % colors.length;
    pick(colors[next]);
    root?.querySelectorAll<HTMLButtonElement>('button')[next]?.focus();
  }
</script>

<div class="sw {size}" role="radiogroup" aria-label={ariaLabel} bind:this={root}>
  {#each colors as color, i (color)}
    {@const on = (value || '').toLowerCase() === color.toLowerCase()}
    <button
      type="button"
      role="radio"
      aria-checked={on}
      aria-label={labels?.[i] ?? color}
      tabindex={i === focusIndex ? 0 : -1}
      style:--c={color}
      onclick={() => pick(color)}
      onkeydown={(e) => keydown(e, i)}
    >
      {#if on}<Check strokeWidth={3} aria-hidden="true" />{/if}
    </button>
  {/each}
</div>

<style>
  .sw { display: flex; flex-wrap: wrap; gap: var(--space-2); }
  button {
    width: 28px;
    height: 28px;
    padding: 0;
    border-radius: var(--radius-sm);
    border: 0;
    display: grid;
    place-items: center;
    background: var(--c);
    color: var(--on-brand);
    box-shadow: inset 0 0 0 1px color-mix(in srgb, var(--text) 10%, transparent);
    transition: transform var(--dur-fast);
  }
  .sm button { width: 22px; height: 22px; border-radius: var(--radius-xs); }
  button :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  button:hover { transform: scale(1.08); }
  button[aria-checked='true'] { box-shadow: 0 0 0 2px var(--bg-elevated), 0 0 0 4px var(--c); outline: none; }
  button:focus-visible { outline: none; box-shadow: 0 0 0 2px var(--bg-elevated), 0 0 0 4px var(--brand); }
</style>
